import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage"; // default: localStorage if web, AsyncStorage if react-native
import { persistReducer } from "redux-persist";
import { userRequest } from "../api/requestMethods";

const initialState = {
  airtelSseData: null,
  status: "idle",
  error: null,
  trackOrderAirtelSse: null,
  verificationData: null,
  verificationStatus: "idle",
  verificationError: null,
};

const verifyAirtelPayment = createAsyncThunk(
  "airtelSse/verifyAirtelPayment",
  async (params, { rejectWithValue }) => {
    try {
      const resp = await userRequest.post(
        `/fw/verify_airtel_payment-status/${params.userId}`,
        { respObj: params.respObj }
      );

      // ── PENDING IS NOT SUCCESS ────────────────────────────────────────
      // The server used to answer 200 {status:"success"} for ANY charge that
      // was not literally "failed" — including "pending", the state a charge
      // sits in while the customer is still looking at their PIN prompt. It
      // now distinguishes them: 200 for settled, 202 for still-in-flight,
      // 400 for declined.
      //
      // Axios resolves on any 2xx, so without this branch a 202 would land in
      // `fulfilled` and this screen would render "Payment verified" over an
      // order that has not been paid for — reintroducing, on the web, exactly
      // the bug the 202 was added to remove. `settled` is what the UI reads.
      return {
        ...resp.data,
        settled: resp.status === 200 && resp.data?.status === "success",
        pending: resp.status === 202 || resp.data?.status === "pending",
      };
    } catch (error) {
      const payload = error.response?.data;
      // Flutterwave's own words where we have them — "Insufficient funds",
      // "Transaction expired", a wrong PIN — never a guess.
      return rejectWithValue(payload?.message || error.message);
    }
  }
);

const airtelSseSlice = createSlice({
  name: "airtelSse",
  initialState,
  reducers: {
    setAirtelSSEData: (state, action) => {
      state.airtelSseData = action.payload;
    },
    setAirtelTrackOrderSSEData: (state, action) => {
      state.trackOrderAirtelSse = action.payload;
    },
    clearAirtelSSEData: (state) => {
      state.airtelSseData = null;
      state.trackOrderAirtelSse = null;
      state.verificationData = null;
      state.verificationError = null;
      // This slice is redux-persisted, so a stale "failed" from a previous
      // attempt survived a reload and made a brand-new payment render as
      // already-declined.
      state.verificationStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyAirtelPayment.pending, (state, action) => {
        state.verificationStatus = "loading";
        state.verificationData = action.meta.arg.respObj;
      })
      .addCase(verifyAirtelPayment.fulfilled, (state, action) => {
        // Three outcomes, not two. "pending" keeps the screen honest while the
        // poll in AirtelPayScreen waits for a real answer.
        // Default to "pending", never "succeeded". Anything that is neither
        // explicitly settled nor explicitly pending is something we do not
        // understand, and in a payment state machine the safe unknown is
        // "keep waiting" — the watchdog resolves it. Defaulting to success is
        // one proxy rewrite away from being the bug the 202 removed.
        state.verificationStatus = action.payload?.settled ? "succeeded" : "pending";
        state.verificationData = action.payload;
      })
      .addCase(verifyAirtelPayment.rejected, (state, action) => {
        state.verificationStatus = "failed";
        state.verificationError = action.payload;
      });
  },
});

const persistConfig = {
  key: "airtelSse",
  storage,
};
const persistedReducer = persistReducer(persistConfig, airtelSseSlice.reducer);

export default persistedReducer;

export const {
  setAirtelSSEData,
  setAirtelTrackOrderSSEData,
  clearAirtelSSEData,
} = airtelSseSlice.actions;

export { verifyAirtelPayment };
